import React, { useEffect, useState } from 'react';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import CssBaseline from '@mui/material/CssBaseline';
import TextField from '@mui/material/TextField';
import Link from '@mui/material/Link';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import Typography from '@mui/material/Typography';
import { makeStyles } from '@mui/styles';
import Container from '@mui/material/Container';

import { getApp } from "firebase/app";
import { getAuth, isSignInWithEmailLink, signInWithEmailLink } from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";

const ACCOUNT_DELETION_HOSTNAME = /^support\.[^.]+\.scouthealth\.com$/;
const EMAIL_STORAGE_KEY = 'accountDeletionEmail';

const steps = {
  init: 'init',
  enterEmail: 'enterEmail',
  linkSent: 'linkSent',
  enterDetails: 'enterDetails',
  submitting: 'submitting',
  success: 'success',
};

export function isAccountDeletionPage() {
  return ACCOUNT_DELETION_HOSTNAME.test(window.location.hostname);
}

export function AccountDeletion() {
  const classes = useStyles();

  const [step, setStep] = useState(steps.init);
  const [email, setEmail] = useState('');
  const [confirmEmailInput, setConfirmEmailInput] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const auth = getAuth(getApp());

  // On mount: check if this page load is the magic-link callback
  useEffect(() => {
    if (isSignInWithEmailLink(auth, window.location.href)) {
      const savedEmail = window.localStorage.getItem(EMAIL_STORAGE_KEY);
      if (savedEmail) {
        completeSignIn(savedEmail);
      } else {
        // Link opened in a different browser — go straight to form
        // User can provide their email there
        setStep(steps.enterDetails);
      }
    } else {
      setStep(steps.enterEmail);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function completeSignIn(emailToUse) {
    try {
      await signInWithEmailLink(auth, emailToUse, window.location.href);
      window.localStorage.removeItem(EMAIL_STORAGE_KEY);
      // Remove the Firebase action code params from the URL
      window.history.replaceState({}, document.title, window.location.pathname);
      setEmail(emailToUse);
      setStep(steps.enterDetails);
    } catch (error) {
      console.error('signInWithEmailLink error:', error);
      setErrorMessage('The verification link is invalid or has expired. Please request a new one.');
      setStep(steps.enterEmail);
    }
  }

  async function handleSendLink(e) {
    e.preventDefault();
    if (!validateEmail(email)) {
      setErrorMessage('Please enter a valid email address.');
      return;
    }
    setErrorMessage('');
    try {
      const functions = getFunctions(getApp());
      const sendSignInEmail = httpsCallable(functions, 'sendCustomSignInEmail');
      await sendSignInEmail({ 
        email
      });
      window.localStorage.setItem(EMAIL_STORAGE_KEY, email);
      setStep(steps.linkSent);
    } catch (error) {
      console.error('sendCustomSignInEmail error:', error);
      setErrorMessage('Failed to send verification email. Please try again later.');
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!name.trim() || !phone.trim()) {
      setErrorMessage('All fields are required.');
      return;
    }
    if (!isValidPhoneNumber(phone)) {
      setErrorMessage('Please enter a valid phone number.');
      return;
    }

    // If email not set (link opened in different browser), require it
    let emailToUse = email;
    if (!emailToUse && isSignInWithEmailLink(auth, window.location.href)) {
      if (!validateEmail(confirmEmailInput)) {
        setErrorMessage('Please enter a valid email address.');
        return;
      }
      emailToUse = confirmEmailInput.trim();
      // Complete sign-in with the provided email
      try {
        await signInWithEmailLink(auth, emailToUse, window.location.href);
        window.history.replaceState({}, document.title, window.location.pathname);
        setEmail(emailToUse);
      } catch (error) {
        console.error('signInWithEmailLink error:', error);
        setErrorMessage('The verification link is invalid or has expired. Please request a new one.');
        setStep(steps.enterEmail);
        return;
      }
    }

    setErrorMessage('');
    setStep(steps.submitting);
    try {
      const functions = getFunctions(getApp());
      const submitRequest = httpsCallable(functions, 'submitAccountDeletionRequest');
      await submitRequest({
        name: name.trim(),
        phone: phone.trim(),
      });
      setStep(steps.success);
    } catch (error) {
      console.error('submitAccountDeletionRequest error:', error);
      setErrorMessage('An error occurred while submitting your request. Please try again later.');
      setStep(steps.enterDetails);
    }
  }

  function validateEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }

  function isValidPhoneNumber(value) {
    // Remove all non-digit characters
    const digitsOnly = value.replace(/\D/g, '');
    // Valid if it has 10 or more digits (supports US and international formats)
    return digitsOnly.length >= 10;
  }

  // ── Step: initializing (checking magic link) ──────────────────────────────
  if (step === steps.init) {
    return (
      <Container component="main" maxWidth="xs">
        <CssBaseline />
        <div className={classes.paper}>
          <CircularProgress />
        </div>
      </Container>
    );
  }

  // ── Step: success ─────────────────────────────────────────────────────────
  if (step === steps.success) {
    return (
      <Container component="main" maxWidth="xs">
        <CssBaseline />
        <div className={classes.paper}>
          <Avatar className={classes.avatar}>
            <DeleteForeverIcon />
          </Avatar>
          <Typography component="h1" variant="h5">
            Request Submitted
          </Typography>
          <br />
          <Typography component="p" variant="body1" align="center">
            Your account deletion request has been received. We will delete your
            account and remove your personally identifiable information (PII) and
            protected health information (PHI) where legally permitted within 30 days.
            Test data may be retained for quality assurance and regulatory compliance.
          </Typography>
          <br />
          <Typography component="p" variant="body2" align="center">
            For questions, contact{' '}
            <Link href="mailto:support@scouthealth.com">
              support@scouthealth.com
            </Link>
            .
          </Typography>
        </div>
        <br />
        <Copyright />
      </Container>
    );
  }

  // ── Step: link sent ───────────────────────────────────────────────────────
  if (step === steps.linkSent) {
    return (
      <Container component="main" maxWidth="xs">
        <CssBaseline />
        <div className={classes.paper}>
          <Avatar className={classes.avatar}>
            <MarkEmailReadIcon />
          </Avatar>
          <Typography component="h1" variant="h5">
            Check Your Email
          </Typography>
          <br />
          <Typography component="p" variant="body1" align="center">
            A verification link has been sent to <strong>{email}</strong>.
            Click the link in that email to confirm your identity and continue.
          </Typography>
          <br />
          <Typography component="p" variant="body2" align="center">
            The link expires in 1 hour. If you don't see the email, check your
            spam folder.
          </Typography>
        </div>
        <br />
        <Copyright />
      </Container>
    );
  }


  // ── Step: enter details (post-verification) ───────────────────────────────
  if (step === steps.enterDetails || step === steps.submitting) {
    return (
      <Container component="main" maxWidth="xs">
        <CssBaseline />
        <div className={classes.paper}>
          <Avatar className={classes.avatar}>
            <DeleteForeverIcon />
          </Avatar>
          <Typography component="h1" variant="h5">
            Delete Account
          </Typography>
          <br />
          <Typography component="p" variant="body2" align="center">
            Your email has been verified. Please provide the following
            information to complete your deletion request. Your account will be
            deleted and your PII/PHI removed where legally allowed within 30 days.
          </Typography>
          <form className={classes.form} onSubmit={handleSubmit} noValidate>
            {!email && (
              <TextField
                variant="outlined"
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoComplete="email"
                autoFocus
                value={confirmEmailInput}
                onChange={e => setConfirmEmailInput(e.target.value)}
                disabled={step === steps.submitting}
              />
            )}
            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              id="name"
              label="Full Name"
              name="name"
              autoComplete="name"
              autoFocus={!!email}
              value={name}
              onChange={e => setName(e.target.value)}
              disabled={step === steps.submitting}
            />
            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              id="phone"
              label="Phone Number"
              name="phone"
              autoComplete="tel"
              type="tel"
              value={phone}
              onChange={e => setPhone(e.target.value)}
              disabled={step === steps.submitting}
            />
            <br />
            <br />
            {errorMessage && (
              <Typography component="p" variant="body2" color="error">
                {errorMessage}
              </Typography>
            )}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="secondary"
              className={classes.submit}
              disabled={step === steps.submitting}
            >
              {step === steps.submitting
                ? <CircularProgress size={24} color="inherit" />
                : 'Submit Deletion Request'}
            </Button>
          </form>
        </div>
        <br />
        <Copyright />
      </Container>
    );
  }

  // ── Step: enter email (initial state) ─────────────────────────────────────
  return (
    <Container component="main" maxWidth="xs">
      <CssBaseline />
      <div className={classes.paper}>
        <Avatar className={classes.avatar}>
          <DeleteForeverIcon />
        </Avatar>
        <Typography component="h1" variant="h5">
          Delete Account
        </Typography>
        <br />
        <Typography component="p" variant="body2" align="center">
          To protect your account, we'll send a verification link to your email
          address before processing your request.
        </Typography>
        <form className={classes.form} onSubmit={handleSendLink} noValidate>
          <TextField
            variant="outlined"
            margin="normal"
            required
            fullWidth
            id="email"
            label="Email Address"
            name="email"
            autoComplete="email"
            type="email"
            autoFocus
            value={email}
            onChange={e => setEmail(e.target.value)}
          />
          {errorMessage && (
            <Typography component="p" variant="body2" color="error">
              {errorMessage}
            </Typography>
          )}
          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="secondary"
            className={classes.submit}
          >
            Send Verification Link
          </Button>
        </form>
      </div>
      <br />
      <Copyright />
    </Container>
  );
}

function Copyright() {
  return (
    <Typography variant="body2" color="textSecondary" align="center">
      {'Copyright © '}
      <Link color="inherit" href="#">
        Scout Health
      </Link>{' '}
      {new Date().getFullYear()}
      {'.'}
    </Typography>
  );
}

const useStyles = makeStyles((theme) => ({
  paper: {
    marginTop: theme.spacing(8),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  avatar: {
    margin: theme.spacing(1),
    backgroundColor: theme.palette.secondary.main,
  },
  form: {
    width: '100%',
    marginTop: theme.spacing(1),
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
  },
}));
